import { useCallback, useState } from 'react';
import { useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { useFileUploader } from '@ts-core/core/hooks/utils';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';

import { useStudentAuth } from 'modules/account/hooks';

import { StudentActivity } from '@ts-core/modules/activities/types';

import {
  createActivityAnswerMutation,
  CreateActivityAnswerMutationResult,
  CreateActivityAnswerMutationVariables,
} from '../graphql/mutations/createActivityAnswerMutation';
import {
  deleteActivityAnswerMutation,
  DeleteActivityAnswerMutationResult,
  DeleteActivityAnswerMutationVariables,
} from '../graphql/mutations/deleteActivityAnswerMutation';

export interface ActivityActionsHookInput {
  courseId: string;
  activityId: string;
}

export interface ActivityActionsHookPayload {
  file?: File;
  onFileSelected: (file: File) => void;

  textAnswer?: string;
  onTextAnswerChanged: (text: string) => void;

  onAnswerActivity: () => void;
  answerActivityLoading: boolean;
  answerActivityError?: string;

  onDeleteAnswer: (activity: StudentActivity) => void;
}

export default function useActivityActions(input: ActivityActionsHookInput): ActivityActionsHookPayload {
  const { courseId, activityId } = input;
  const { userId } = useStudentAuth();
  const [file, setSelectedFile] = useState<File>();
  const [textAnswer, setTextAnswer] = useState<string>();
  const [clientError, setClientError] = useState<string>();
  const { onUploadFile, error: uploadFileError, uploading: uploadingFile } = useFileUploader();

  const { showSuccessNotification, showErrorNotification } = useSnackbarNotifications();

  const [createAnswer, { loading: createAnswerLoading, error: createAnswerError }] = useMutation<
    CreateActivityAnswerMutationResult,
    CreateActivityAnswerMutationVariables
  >(createActivityAnswerMutation, {
    onCompleted: () => {
      setSelectedFile(undefined);
      setTextAnswer(undefined);
      showSuccessNotification('Tu respuesta fue enviada correctamente.');
    },
  });
  const onAnswerActivity = useCallback(async () => {
    function onCreateAnswer(fileInput?: { path: string; name: string }, text?: string) {
      createAnswer({
        variables: {
          courseId,
          activityId,
          input: {
            file: fileInput,
            text,
          },
        },
      });
    }

    if (!file && (!textAnswer || textAnswer.length === 0)) {
      setClientError('Debes seleccionar un archivo o escribir una respuesta.');
      return;
    }
    setClientError(undefined);
    if (file) {
      onUploadFile(
        file,
        'aws/signed-url/activities',
        { courseId, activityId, fileName: file.name, studentId: userId },
        {
          onSuccess: (data) => {
            onCreateAnswer({ path: data.path, name: file.name }, textAnswer);
          },
        }
      );
    } else {
      onCreateAnswer(undefined, textAnswer);
    }

    // Upload file
  }, [file, textAnswer, courseId, activityId, userId, createAnswer, onUploadFile]);

  const [deleteAnswer] = useMutation<DeleteActivityAnswerMutationResult, DeleteActivityAnswerMutationVariables>(deleteActivityAnswerMutation, {
    onError: showErrorNotification,
  });
  const onDeleteAnswer = useCallback(
    (activity: StudentActivity) => {
      if (activity.answer) {
        deleteAnswer({
          variables: {
            courseId,
            activityId,
            answerId: activity.answer.id,
          },
          optimisticResponse: {
            activity: {
              ...activity,
              answer: undefined,
            },
          },
        });
      }
    },
    [deleteAnswer, courseId, activityId]
  );

  return {
    file,
    onFileSelected: setSelectedFile,

    textAnswer,
    onTextAnswerChanged: setTextAnswer,

    onAnswerActivity,
    answerActivityLoading: uploadingFile || createAnswerLoading,
    answerActivityError: clientError || uploadFileError || getErrorMessage({ error: createAnswerError }),

    onDeleteAnswer,
  };
}
